import { useCallback, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { dohvatiRaspored } from "@/lib/gradilista";
import { workflow } from "@/lib/workflow";
import { Feedback, useResource } from "@/components/workflow/shared";
import { CrewBoard } from "./site-roster/CrewBoard";
import { TaskTimeline } from "./site-roster/TaskTimeline";
import { SiteEquipment } from "./oprema/SiteEquipment";
import { EquipmentPlaceView } from "./site-equipment/EquipmentPlaceView";
import { ProcurementList } from "@/pages/ProcurementPage";
import { ConstructionOverview } from "./nabava/ConstructionOverview";
import { hasProcurement } from "@/lib/procurement";

export function SiteWorkTabs({
  siteId,
  date,
  peopleCount,
  children,
  onChanged,
  transitional = false,
}: {
  siteId: string;
  date: string;
  peopleCount: number;
  children: ReactNode;
  onChanged: () => Promise<void>;
  transitional?: boolean;
}) {
  const { can, user } = useAuth();
  const [refreshError, setRefreshError] = useState("");
  const [params, setParams] = useSearchParams();
  const readCrews = can("brigada", "read");
  const readTasks = can("takt", "read");
  const readRoster = can("raspored", "read");
  const readEquipment = can("oprema", "read");
  const readSiteEquipment = can("oprema_gradiliste", "read");
  const readOrders = hasProcurement(user);
  const requested = params.get("kartica");
  const tab =
    requested === "oprema-gradiliste" && readSiteEquipment ? "oprema-gradiliste" : requested === "brigade" && readCrews
      ? "brigade"
      : requested === "taktovi" && readTasks && readCrews
        ? "taktovi"
        : requested === "gradilisni" && readOrders ? "gradilisni" : requested === "narudzbe" && readOrders ? "narudzbe" : requested === "oprema" && readEquipment ? "oprema" : "ljudi";
  const resource = useResource(
    `${siteId}:${readCrews}:${readTasks}:${readRoster}`,
    useCallback(
      async (signal) => {
        const [crews, tasks, roster] = await Promise.all([
          readCrews ? workflow.crews(siteId, signal) : Promise.resolve([]),
          readTasks ? workflow.tasks(siteId, signal) : Promise.resolve([]),
          readRoster
            ? dohvatiRaspored(siteId, undefined, signal)
            : Promise.resolve([]),
        ]);
        return { crews, tasks, roster };
      },
      [siteId, readCrews, readTasks, readRoster],
    ),
  );
  const refresh = () => {
    resource.reload();
    setRefreshError("");
    void onChanged().catch(() =>
      setRefreshError(
        "Promjena je spremljena, ali popis ljudi nije osvježen. Ponovno učitajte stranicu.",
      ),
    );
  };
  return (
    <>
      <nav
        aria-label="Pogledi gradilišta"
        className="flex flex-wrap gap-1 border-b"
      >
        {[
          ["ljudi", `Ljudi (${peopleCount})`, true],
          ["oprema", "OZO", readEquipment],
          ["oprema-gradiliste", "Oprema", readSiteEquipment],
          ["narudzbe", "Narudžbe", readOrders],
          ["gradilisni", "Gradilišni materijal", readOrders],
          [
            "brigade",
            `Brigade (${resource.data?.crews.filter((c) => c.aktivna).length ?? "…"})`,
            readCrews,
          ],
          [
            "taktovi",
            `Taktovi (${resource.data?.tasks.filter((t) => t.status === "aktivan").length ?? "…"})`,
            readTasks && readCrews,
          ],
        ].map(
          ([id, label, visible]) =>
            visible && (
              <button
                key={String(id)}
                type="button"
                aria-current={tab === id ? "page" : undefined}
                className={`min-h-11 border-b-4 px-4 py-2 ${tab === id ? "border-amber-400 font-bold" : "border-transparent"}`}
                onClick={() => {
                  const next = new URLSearchParams(params);
                  next.set("kartica", String(id));
                  setParams(next);
                }}
              >
                {label}
              </button>
            ),
        )}
      </nav>
      <Feedback error={refreshError} />
      {tab === "oprema-gradiliste" ? <EquipmentPlaceView key={siteId} siteId={siteId} /> : tab === "gradilisni" ? <ConstructionOverview key={siteId} siteId={siteId} /> : tab === "narudzbe" ? <ProcurementList key={siteId} siteId={siteId} /> : tab === "oprema" ? <SiteEquipment key={siteId} siteId={siteId} /> : tab === "ljudi" ? (
        children
      ) : (
        <>
          <Feedback error={resource.error} />
          {resource.loading && (
            <p role="status">Učitavanje brigada i taktova…</p>
          )}
          {resource.data &&
            (tab === "brigade" ? (
              <CrewBoard
                key={`${siteId}:${date}`}
                siteId={siteId}
                date={date}
                {...resource.data}
                refresh={refresh}
                readRoster={readRoster}
                transitional={transitional}
              />
            ) : (
              <TaskTimeline
                key={siteId}
                siteId={siteId}
                date={date}
                {...resource.data}
                refresh={refresh}
              />
            ))}
        </>
      )}
    </>
  );
}
